import { useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  useGetOAuthClient,
  useCreateOAuthClient,
  useRotateOAuthClient,
  useRevokeOAuthClient,
} from "../../../hooks/queries/useMerchantOpenApiQueries";
import { toastService } from "../../../providers";
import { extractAxiosErrorMessage } from "../../../services/utils/errorUtils";

export const useApiCredentials = (apiEnabled) => {
  const {
    data: oauthClient,
    isError: isQueryError,
    isLoading: isLoadingCredentials,
    refetch,
  } = useGetOAuthClient(apiEnabled);
  const isFetchError = apiEnabled && isQueryError;
  const { mutate: createOAuthClient, isPending: isCreating } = useCreateOAuthClient();
  const { mutate: rotateOAuthClient, isPending: isRotating } = useRotateOAuthClient();
  const { mutate: revokeOAuthClient, isPending: isRevoking } = useRevokeOAuthClient();

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: { credentialName: "" },
    mode: "onTouched",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [showRotateConfirmation, setShowRotateConfirmation] = useState(false);
  const [showRevokeConfirmation, setShowRevokeConfirmation] = useState(false);
  const [oneTimeSecret, setOneTimeSecret] = useState(null);
  const [secretCopied, setSecretCopied] = useState(false);

  const credentialNameValue = watch("credentialName");
  const hasCredentials = !isFetchError && !!oauthClient?.public_id;
  const isBusy = isCreating || isRotating || isRevoking;

  const navigationBlocker = useBlocker(!!oneTimeSecret);

  useEffect(() => {
    if (!oneTimeSecret) return;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [oneTimeSecret]);

  const handleCopy = () => {
    navigator.clipboard.writeText(oauthClient.client_id);
    toastService.info("Client ID copied to clipboard");
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(oneTimeSecret);
    setSecretCopied(true);
    toastService.info("Secret copied to clipboard");
  };

  const handleDismissSecret = () => {
    setOneTimeSecret(null);
    setSecretCopied(false);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    reset();
  };

  const handleCreate = (data) => {
    createOAuthClient(
      { name: data.credentialName },
      {
        onSuccess: (response) => {
          setOneTimeSecret(response.client_secret);
          setModalOpen(false);
          reset();
          toastService.success("API credentials generated successfully");
        },
        onError: (error) => {
          toastService.error(
            extractAxiosErrorMessage(error, "Failed to generate credentials"),
          );
        },
      },
    );
  };

  const handleConfirmRotate = () => {
    rotateOAuthClient(oauthClient.public_id, {
      onSuccess: (response) => {
        setOneTimeSecret(response.client_secret);
        setShowRotateConfirmation(false);
        toastService.success("API credentials rotated successfully");
      },
      onError: (error) => {
        toastService.error(
          extractAxiosErrorMessage(error, "Failed to rotate credentials"),
        );
      },
    });
  };

  const handleConfirmRevoke = () => {
    revokeOAuthClient(oauthClient.public_id, {
      onSuccess: () => {
        setShowRevokeConfirmation(false);
        toastService.success("API credentials revoked successfully");
      },
      onError: (error) => {
        toastService.error(
          extractAxiosErrorMessage(error, "Failed to revoke credentials"),
        );
      },
    });
  };

  return {
    control,
    oauthClient,
    hasCredentials,
    isLoadingCredentials,
    isFetchError,
    onRetry: refetch,
    isCreating,
    isRotating,
    isRevoking,
    isBusy,
    modalOpen,
    showRotateConfirmation,
    showRevokeConfirmation,
    oneTimeSecret,
    secretCopied,
    credentialNameValue,
    onOpenModal: () => setModalOpen(true),
    onModalClose: handleModalClose,
    onSubmit: handleSubmit(handleCreate),
    onCopy: handleCopy,
    onCopySecret: handleCopySecret,
    onDismissSecret: handleDismissSecret,
    onRotateClick: () => setShowRotateConfirmation(true),
    onCancelRotate: () => setShowRotateConfirmation(false),
    onConfirmRotate: handleConfirmRotate,
    navigationBlocker,
    onRevokeClick: () => setShowRevokeConfirmation(true),
    onCancelRevoke: () => setShowRevokeConfirmation(false),
    onConfirmRevoke: handleConfirmRevoke,
  };
};
